import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Col, Input, notification, Row, Table } from 'antd';
import { CSVLink } from 'react-csv';
import { add, identity, isEmpty, isNil, isString, noop } from 'lodash';
import { ColumnType, TableProps } from 'antd/lib/table';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { FilterOutlined, SaveOutlined } from '@ant-design/icons';
import './styles.css';
import { useDisplayFrameworkContext } from 'DisplayFramework/hooks/useDisplayFrameworkContext';
import Title from 'components/Title';
import ClearButton from 'components/ClearButton';
import SearchButton from 'components/SearchButton';
import ExtractButton from 'components/ExtractButton';
import GetLabel from 'DisplayFramework/components/GetLabel';
import { AuthContext } from 'components/Auth/Auth';
import {
  CompDisplayResponse,
  ParPagComponentRequest,
  updatePageComponent,
} from 'services/displayFramework';
import { usePaginatorItems } from 'hooks/usePaginatorItems';

export type DataGridColDef<RecordType = any> = ColumnType<RecordType> & {
  filtrable?: boolean | string;
  customfilter?: boolean;
  filtrablePrincipal?: boolean;
  filterComponent?: React.ReactElement;
  comcode?: string;
  valueFormatter?: (value: any) => any;
  children?: Array<any>;
  filterComponentStyle?: React.CSSProperties;
  spanprops?: number | string;
};

export type DataGridProps<RecordType> = Pick<
  TableProps<RecordType>,
  | 'components'
  | 'dataSource'
  | 'pagination'
  | 'rowKey'
  | 'summary'
  | 'rowSelection'
  | 'expandable'
  | 'scroll'
  | 'size'
  | 'onRow'
> & {
  columns: DataGridColDef<RecordType>[];
  onSearch?: SubmitHandler<any>;
  onClear?: any;
  initialSearchValues?: any;
  extraActions?: Iterable<React.ReactNode>;
  titleComcode?: string;
  isLoading?: boolean;
  extractData?: string;
  bordered?: boolean;
  title?: any;
  header?: Iterable<React.ReactNode>;
  spanFilter?: Array<number>;
  hideFilters?: boolean;
  hideExtraAction?: boolean;
  componentName?: string;
  hideHeader?: boolean;
  disabledSearchButton?: boolean;
  hideTitle?: boolean;
  hideMarginExtractData?: boolean;
  spanprops?: number | string;
  className?: string;
};

const DataGrid = <RecordType extends object = any>(
  props: DataGridProps<RecordType>
) => {
  const {
    spanprops,
    bordered = false,
    columns: columnsProp,
    dataSource = [],
    extraActions,
    initialSearchValues,
    isLoading,
    disabledSearchButton,
    extractData,
    onSearch = noop,
    onClear = noop,
    summary,
    rowKey,
    rowSelection,
    expandable,
    scroll: scrollProp = {},
    size = 'middle',
    title: titleProp,
    header,
    titleComcode,
    spanFilter,
    hideFilters = false,
    hideExtraAction = false,
    componentName,
    hideHeader = false,
    hideTitle,
    hideMarginExtractData,
    className,
    ...otherProps
  } = props;

  const { handleSubmit, control, reset, formState, setValue } = useForm({
    defaultValues: initialSearchValues,
    shouldUnregister: false,
  });

  
  const { isInspect } = useContext(AuthContext);
  const { getComponentLabel, getComponentConfiguration, pagcode, refresh } =
    useDisplayFrameworkContext();

  const handleSaveNewLabel = (comcode: string, label: string) => {
    const request: ParPagComponentRequest = {
      comcode: comcode ?? '',
      comformat: '',
      comhint: '',
      comlabel: label,
      commessage: '',
      complaceholder: '',
      comrequired: false,
      comstyle: '',
      comtype: '',
      pagcode: pagcode ?? '',
    };
    updatePageComponent({ request: request }).then((res) => {
      if (res.code === '200') {
        notification.success({
          message: res.message,
          description: res.description,
        });
        refresh();
      } else {
        notification.error({
          message: res.message,
          description: res.description,
        });
      }
    });
  };

  const columns = useMemo<ColumnType<RecordType>[]>(() => {
    return columnsProp.map(
      ({
        filtrable,
        dataIndex,
        children,
        comcode: comcodeProp,
        title,
        key,
        filterComponent,
        filterComponentStyle,
        render: renderProp,
        valueFormatter = identity,
        ...column
      }) => {
        const comcode = comcodeProp ?? (dataIndex as string) ?? (key as string);
        let label: string | any;
        let comp: CompDisplayResponse | undefined;
        comp = getComponentConfiguration(comcode);

        if (title) {
          label = title as string;
        } else {
          label = !isEmpty(comp?.comlabel ?? '')
            ? comp?.comlabel
            : '##' + comcode;
        }

        const render: ColumnType<RecordType>['render'] = (
          value,
          record,
          index
        ) => {
          if (renderProp) {
            return renderProp(valueFormatter(value), record, index);
          } else {
            return valueFormatter(value);
          }
        };
        let newLabel = '';

        return {
          ...column,
          dataIndex,
          children,
          key,
          render,
          className: comp?.flagdisabled ? 'disabled-column' : '',
          fixed: isInspect ? undefined : column?.fixed,
          width: comp?.flaghide ? 'hide' : isInspect ? 220 : column?.width,
          title: () =>
            isInspect ? (
              <Input
                defaultValue={label}
                size="middle"
                onChange={(event) => {
                  newLabel = event.target.value;
                }}
                className="input-inspect"
                addonAfter={
                  <SaveOutlined
                    onClick={() => {
                      handleSaveNewLabel(
                        comcode,
                        newLabel === '' ? label : newLabel
                      );
                    }}
                  />
                }
              />
            ) : (
              <span>
                {comp?.flagrequired && (
                  <span
                    style={{ color: 'red', fontSize: 18, marginRight: '5px' }}
                  >
                    *
                  </span>
                )}
                <span>{label}</span>
              </span>
            ),
        };
      }
    );
  }, [isInspect ? false : columnsProp, getComponentLabel, isInspect]);

  const [showMore, setShowMore] = useState(false);

  const columnsWithFilters = useMemo(() => {
    return columnsProp.filter((column) => column.filtrable ?? false);
  }, [columnsProp]);

  const changeValuesForm = (val: any) => {
    let listnameFilter = columnsWithFilters?.map((filter) =>
      isString(filter.filtrable)
        ? String(filter.filtrable)
        : (filter.dataIndex as any)
    );
    listnameFilter?.forEach((key) => {
      setValue(key, val[key]);
    });
  };

  useEffect(() => {
    if (localStorage.getItem('searchParams_components') === componentName) {
      if (localStorage.getItem('searchParams') !== null) {
        let values: any = JSON?.parse(
          localStorage?.getItem('searchParams') ??
            JSON?.stringify(initialSearchValues) ??
            ''
        );

        changeValuesForm(values);
        setShowMore(true);
      }
    }
  }, []);

  const resetValuesForm = () => {
    onClear();
    localStorage.removeItem('searchParams');
    localStorage.removeItem('searchParams_components');
    changeValuesForm(initialSearchValues);
  };

  const title = useCallback(
    () => (
      <div className="title">
        {titleProp ? (
          <>
            <Col span={21} style={{ paddingBottom: 5 }}>
              <h3
                style={{
                  fontWeight: 'bold',
                  marginBottom: 0,
                  paddingBottom: 0,
                  fontSize: '20px',
                }}
              >
                {titleProp}
              </h3>
            </Col>
            <Col span={3} style={{ textAlign: 'right' }}>
              {hideFilters ? (hideExtraAction ? null : extraActions) : null}
            </Col>
          </>
        ) : titleComcode ? (
          titleComcode && (
            <>
              <Col span={20}>
                <Title
                  comcode={titleComcode}
                  level={4}
                  style={{ marginBottom: 0 }}
                />
              </Col>
              <Col span={4} style={{ textAlign: 'right' }}>
                {hideFilters ? (hideExtraAction ? null : extraActions) : null}
              </Col>
            </>
          )
        ) : (
          <>
            <Col span={21} />
            <Col span={3} style={{ textAlign: 'right' }}>
              {hideFilters ? (hideExtraAction ? null : extraActions) : null}
            </Col>
          </>
        )}
        <div style={{ flexGrow: 1 }} />
      </div>
    ),
    [titleProp, hideFilters, hideExtraAction, extraActions, titleComcode]
  );
  const [state] = useState({ count: 0 });
  state.count = 0;
  const filters = useCallback(
    () => (
      <Row style={className=="no-border-radius"?{}:{ marginBottom: 5 }} justify="end">
        {hideFilters === true && <Col span={21} />}
        {columnsWithFilters.length >= 1 && hideFilters === false && (
          <>
            <Col
              span={hideExtraAction === true || !extraActions ? 20 : 16}
              style={{ paddingRight: 5, paddingBottom: 5 }}
            >
              <div
                style={{
                  height: '100%',
                  background: '#fff',
                  padding: '12px 15px',
                  boxShadow: ' 0px 0px 7px rgba(0, 0, 0, 0.09)',
                  borderRadius: '7px',
                }}
              >
                <Row>
                  <Title level={4} comcode={titleComcode ?? ''} />
                </Row>
                <Row>
                  <Col span={24}>
                    <Row>
                      {columnsWithFilters.map((filter, index) => {
                        let span = spanFilter
                          ? spanFilter[state.count]
                          : spanprops
                          ? spanprops
                          : 6;
                        state.count = state.count + 1;
                        let newLabel = '';
                        return (
                          (index <= 3 ||
                            index <= Number(spanprops) ||
                            showMore) && (
                            <Col
                              key={filter.key}
                              span={span}
                              style={{
                                width: '100%',
                                paddingRight: 10,
                                fontWeight: 'bold',
                              }}
                            >
                              <span style={{ display: 'block' }}>
                                {isInspect ? (
                                  <Input
                                    defaultValue={getComponentLabel(
                                      isString(filter.filtrable)
                                        ? String(filter.filtrable)
                                        : (filter.dataIndex as any)
                                    )}
                                    size="middle"
                                    onChange={(event) => {
                                      newLabel = event.target.value.concat('');
                                    }}
                                    className="input-inspect"
                                    addonAfter={
                                      <SaveOutlined
                                        onClick={() => {
                                          handleSaveNewLabel(
                                            isString(filter.filtrable)
                                              ? String(filter.filtrable)
                                              : (filter.dataIndex as any),
                                            newLabel === ''
                                              ? getComponentLabel(
                                                  isString(filter.filtrable)
                                                    ? String(filter.filtrable)
                                                    : (filter.dataIndex as any)
                                                )
                                              : newLabel
                                          );
                                        }}
                                      />
                                    }
                                  />
                                ) : (
                                  getComponentLabel(
                                    isString(filter.filtrable)
                                      ? String(filter.filtrable)
                                      : (filter.dataIndex as any)
                                  )
                                )}
                              </span>
                              {filter['filterComponent']?.props?.children
                                ?.length > 1 && !filter.customfilter ? (
                                filter['filterComponent']?.props.children?.map(
                                  (comp: any, index: number) => {
                                    return (
                                      <Controller
                                        key={index}
                                        name={
                                          !isNil(comp?.props?.name)
                                            ? String(comp?.props?.name)
                                            : ''
                                        }
                                        control={control}
                                        as={React.cloneElement(comp, {
                                          style: {
                                            width: '100%',
                                            ...filter.filterComponentStyle,
                                          },
                                        })}
                                      />
                                    );
                                  }
                                )
                              ) : (
                                <Controller
                                  name={
                                    isString(filter.filtrable)
                                      ? (filter.filtrable as string)
                                      : (filter.dataIndex as string)
                                  }
                                  control={control}
                                  as={
                                    filter['filterComponent'] ? (
                                      React.cloneElement(
                                        filter['filterComponent'],
                                        {
                                          style: {
                                            width: '100%',
                                            ...filter.filterComponentStyle,
                                          },
                                        }
                                      )
                                    ) : (
                                      <Input />
                                    )
                                  }
                                />
                              )}
                            </Col>
                          )
                        );
                      })}
                      {columnsWithFilters.length > 4 && !spanprops && (
                        <Col span={24}>
                          <Row justify="end">
                            <a
                              onClick={(ev) => {
                                ev.preventDefault();
                                if (!isInspect) setShowMore(!showMore);
                              }}
                              style={{
                                textDecoration: 'underline',
                                marginRight: '10px',
                                marginTop: '7px',
                              }}
                            >
                              <FilterOutlined />
                              <GetLabel
                                label={
                                  !showMore
                                    ? 'showAdvancedFilter'
                                    : 'hideAdvancedFilter'
                                }
                              />
                            </a>
                          </Row>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col span={4} style={{ paddingBottom: 5 }}>
              <div
                style={{
                  height: '100%',
                  background: '#fff',
                  padding: '12px 15px',
                  boxShadow: ' 0px 0px 7px rgba(0, 0, 0, 0.09)',
                  borderRadius: '7px',
                }}
              >
                {/* <Card type="inner" style={{ height: '100%', width: '100%' }}> */}
                <SearchButton
                  htmlType="submit"
                  comcode="search"
                  disabled={isLoading || disabledSearchButton}
                  Inspectstyle={{ width: '80%' }}
                  onClick={
                    !isInspect
                      ? handleSubmit((e) => {
                          componentName &&
                            localStorage.setItem(
                              'searchParams_components',
                              componentName
                            );
                          componentName &&
                            localStorage.setItem(
                              'searchParams',
                              JSON.stringify(e)
                            );
                          onSearch(e);
                        })
                      : (null as any)
                  }
                  type="primary"
                />
                <Row style={{ height: '20px' }} gutter={18}>
                  &nbsp;
                </Row>
                <ClearButton comcode="clear" onClick={resetValuesForm} />
              </div>
            </Col>
          </>
        )}

        {columnsWithFilters.length === 0 && !hideTitle && (
          <Col span={21} style={{ paddingLeft: 5, paddingBottom: 5 }}>
            {title}
          </Col>
        )}

        {hideFilters === false &&
        extraActions != null &&
        hideExtraAction === false ? (
          <Col span={4} style={{ paddingLeft: 5, paddingBottom: 5 }}>
            {/* <Card type="inner" style={{ height: '100%' }}> */}
            <div
              style={{
                height: '100%',
                background: '#fff',
                padding: '12px 15px',
                boxShadow: ' 0px 0px 7px rgba(0, 0, 0, 0.09)',
                borderRadius: '7px',
              }}
            >
              {extraActions}
              {extractData && dataSource && dataSource.length > 0 && (
                <>
                  {!hideMarginExtractData && (
                    <>
                      <br />
                      <br />
                    </>
                  )}
                  <CSVLink
                    data={dataSource as any}
                    filename={extractData + '.csv'}
                  >
                    <ExtractButton comcode="extractData" />
                  </CSVLink>
                </>
              )}
            </div>
          </Col>
        ) : null}
      </Row>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hideFilters,
      columnsWithFilters,
      hideExtraAction,
      extraActions,
      rowSelection,
      isLoading,
      handleSubmit,
      onSearch,
      formState.isDirty,
      title,
      extractData,
      dataSource,
      spanFilter,
      state,
      getComponentLabel,
      control,
      reset,
      showMore,
      hideHeader,
      isInspect,
      resetValuesForm,
      changeValuesForm,
      disabledSearchButton,
    ]
  );

  const scroll = useMemo(
    () => ({
      x: columns.length > 0 ? columns.map(getColumnWidth).reduce(add) : 'auto',
      y: 550,
      ...scrollProp,
    }),
    [columns, scrollProp]
  );

  return (
    <>
      {filters()}
      <div>{header}</div>
    
          <Table
            showHeader={!hideHeader}
            sortDirections={['ascend']}
            expandable={expandable}
            className={'DataGrid ' + className ?? ''}
            columns={columns.filter((e) => e.width !== 'hide')}
            dataSource={dataSource}
            loading={isLoading}
            rowKey={rowKey}
            summary={summary}
            rowSelection={rowSelection}
        scroll={scroll}
            size={size}
            title={!hideTitle && props.hideFilters ? title : undefined}
            bordered={bordered}
            {...otherProps}
            rowClassName={(record, index) =>
              index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
            }
          />
      
     
    </>
  );
};

export default DataGrid;

const DEFAULT_COLUMN_WIDTH = 100;
const getColumnWidth = <RecordType extends {}>({
  width,
}: ColumnType<RecordType>) => {
  if (width) {
    if (typeof width === 'number') {
      return width;
    } else {
      return Number(width.replace('px', ''));
    }
  } else {
    return DEFAULT_COLUMN_WIDTH;
  }
};
